import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Percent, Ticket, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { title: "", description: "", discount_percent: 10, coupon_code: "", offer_active: true, coupon_active: true, start_date: "", end_date: "" };

const offerStatus = (o) => {
  const today = new Date().toISOString().slice(0, 10);
  if (!o.offer_active) return { label: "OFF", cls: "bg-gray-100 text-gray-600" };
  if (o.start_date && today < o.start_date) return { label: "Scheduled", cls: "bg-blue-100 text-blue-800" };
  if (o.end_date && today > o.end_date) return { label: "Expired", cls: "bg-red-100 text-red-800" };
  return { label: "Active", cls: "bg-green-100 text-green-800" };
};

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/offers").then((r) => { setOffers(r.data); setLoading(false); }); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ title: o.title, description: o.description || "", discount_percent: o.discount_percent, coupon_code: o.coupon_code, offer_active: !!o.offer_active, coupon_active: !!o.coupon_active, start_date: o.start_date || "", end_date: o.end_date || "" }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Offer title is required"); return; }
    if (!form.coupon_code.trim()) { toast.error("Coupon code is required"); return; }
    const pct = parseFloat(form.discount_percent);
    if (isNaN(pct) || pct < 1 || pct > 95) { toast.error("Discount must be between 1% and 95%"); return; }
    setSaving(true);
    try {
      const payload = { ...form, discount_percent: pct, coupon_code: form.coupon_code.trim().toUpperCase() };
      if (editing) await api.put(`/admin/offers/${editing.id}`, payload);
      else await api.post("/admin/offers", payload);
      toast.success(editing ? "Offer updated" : "Offer created");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete offer "${o.title}" (${o.coupon_code})?`)) return;
    await api.delete(`/admin/offers/${o.id}`);
    toast.success("Offer deleted"); load();
  };

  return (
    <AdminLayout title="Offers & Coupons">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">{offers.length} offers</p>
        <button data-testid="add-offer-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-xl bg-choco text-cream font-semibold px-5 py-3 hover:bg-chocodark transition-colors min-h-[48px]"><Plus size={18} /> Add Offer</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin text-goldsoft" /></div>
      ) : offers.length === 0 ? (
        <div data-testid="offers-empty" className="bg-white rounded-2xl border border-[#E5DFD3] p-12 text-center">
          <Ticket size={40} className="mx-auto text-goldsoft mb-3" />
          <p className="font-display text-xl text-choco">No offers yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create your first offer — it will appear on the website automatically.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {offers.map((o) => {
            const st = offerStatus(o);
            return (
              <div key={o.id} data-testid={`admin-offer-${o.id}`} className="bg-white rounded-2xl border border-[#E5DFD3] p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-semibold text-choco line-clamp-1">{o.title}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{o.description}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-gold/15 text-choco font-bold text-sm px-3 py-1"><Percent size={14} />{o.discount_percent}% OFF</span>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold tracking-wider bg-cream border border-dashed border-goldsoft text-choco rounded-lg px-3 py-1">{o.coupon_code}</span>
                  <span data-testid={`offer-status-${o.id}`} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${o.coupon_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>Coupon {o.coupon_active ? "ON" : "OFF"}</span>
                </div>
                {(o.start_date || o.end_date) && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3"><CalendarDays size={13} /> {o.start_date || "Anytime"} → {o.end_date || "No expiry"}</p>
                )}
                <div className="flex gap-4 mt-4">
                  <button data-testid={`edit-offer-${o.id}`} onClick={() => openEdit(o)} className="inline-flex items-center gap-1 text-sm text-choco hover:text-goldsoft"><Pencil size={15} /> Edit</button>
                  <button data-testid={`delete-offer-${o.id}`} onClick={() => del(o)} className="inline-flex items-center gap-1 text-sm text-destructive hover:opacity-70"><Trash2 size={15} /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-choco">{editing ? "Edit Offer" : "Create Offer"}</DialogTitle>
            <DialogDescription className="sr-only">Offer and coupon details form</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-choco">Offer Title</label>
              <Input data-testid="offer-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Weekend Special" className="mt-1 h-11 bg-white border-[#E5DFD3]" />
            </div>
            <div>
              <label className="text-sm font-medium text-choco">Description</label>
              <Textarea data-testid="offer-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Get 20% OFF this weekend" className="mt-1 bg-white border-[#E5DFD3]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-choco">Discount %</label>
                <Input data-testid="offer-form-percent" type="number" min="1" max="95" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
              </div>
              <div>
                <label className="text-sm font-medium text-choco">Coupon Code</label>
                <Input data-testid="offer-form-code" value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })} placeholder="e.g. WEEK20" className="mt-1 h-11 bg-white border-[#E5DFD3] font-mono uppercase" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-choco">Start Date (optional)</label>
                <Input data-testid="offer-form-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
              </div>
              <div>
                <label className="text-sm font-medium text-choco">Expiry Date (optional)</label>
                <Input data-testid="offer-form-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
              </div>
            </div>
            <div className="flex items-center justify-between bg-white rounded-xl border border-[#E5DFD3] px-4 py-3">
              <div><span className="text-sm font-medium text-choco block">Offer ON</span><span className="text-xs text-muted-foreground">Shown on the website banner</span></div>
              <Switch data-testid="offer-form-offer-active" checked={form.offer_active} onCheckedChange={(v) => setForm({ ...form, offer_active: v })} />
            </div>
            <div className="flex items-center justify-between bg-white rounded-xl border border-[#E5DFD3] px-4 py-3">
              <div><span className="text-sm font-medium text-choco block">Coupon ON</span><span className="text-xs text-muted-foreground">Code works at checkout</span></div>
              <Switch data-testid="offer-form-coupon-active" checked={form.coupon_active} onCheckedChange={(v) => setForm({ ...form, coupon_active: v })} />
            </div>
            <button data-testid="offer-form-save" onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark transition-colors disabled:opacity-60">
              {saving && <Loader2 size={18} className="animate-spin" />} {editing ? "Update Offer" : "Create Offer"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOffers;
